import "./modal_footer.scss";
import { DialogFooter } from "@components/ui/dialog.tsx";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";
import useTranslation from "@hooks/use_translation.ts";
import { useSafePostHog } from "@/hooks/use_posthog";
import { usePage } from "@inertiajs/react";
import { useQueryClient } from "@tanstack/react-query";
import { SnowplowEvent, useSnowplowTracker } from "@hooks/use_tracker.ts";
import { buildChecklistItems } from "@components/profile/profile_checklist_items.ts";
import type { ProfileData } from "@/queries/use_profile.ts";
import type { SectionSource } from "./types.ts";

type Props = {
  section: string;
  onSave: () => boolean | void | Promise<boolean | void>;
  onCancel: () => void;
  saving?: boolean;
  source?: SectionSource;
};

const ModalFooter = ({ section, onSave, onCancel, saving, source = "dashboard" }: Props) => {
  const { t } = useTranslation("user", { keyPrefix: "profile_checklist.modals" });
  const posthog = useSafePostHog();
  const { trackEvent } = useSnowplowTracker();
  const queryClient = useQueryClient();
  const { workPreferencesExperimentEnabled } = usePage<{
    workPreferencesExperimentEnabled?: boolean;
  }>().props;

  const trackDashboardOnly = source === "dashboard";

  const handleSave = async () => {
    posthog?.capture("profile_button_clicked", { button: "save", section, source });

    const sectionStatus = () => {
      const profile = queryClient.getQueryData<ProfileData>(["profile"]);
      return profile
        ? buildChecklistItems(profile).find((item) => item.key === section)?.status
        : undefined;
    };

    const oldStatus = trackDashboardOnly ? sectionStatus() : undefined;
    if ((await onSave()) === false) return;

    if (trackDashboardOnly) {
      trackEvent(SnowplowEvent.ProfileWidgetButtonClicked, {
        section,
        button: "save",
        old_status: oldStatus,
        new_status: sectionStatus(),
        variant: workPreferencesExperimentEnabled ? "profile_b" : "profile_a",
      });
    }
  };

  const handleCancel = () => {
    posthog?.capture("profile_button_clicked", { button: "cancel", section, source });
    onCancel();
  };

  return (
    <div className="flex flex-col gap-4">
      <hr className="modal_footer_divider" />
      <DialogFooter className="flex-row justify-end items-center">
        <SecondaryButton size="medium" onClick={handleCancel}>
          {t("cancel")}
        </SecondaryButton>
        <PrimaryButton size="medium" loading={saving} onClick={handleSave}>
          {t("save")}
        </PrimaryButton>
      </DialogFooter>
    </div>
  );
};

export default ModalFooter;
